import React from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, ShieldCheckIcon, AlertTriangleIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';

interface CompleteValidationToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

const CompleteValidationToolCallComponent: React.FC<CompleteValidationToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  let validationStatus = '';
  let summary = '';
  try {
    const parsed = typeof args === 'object' && args ? args : JSON.parse(argsText);
    validationStatus = parsed?.status || '';
    summary = parsed?.summary || '';
  } catch {
    validationStatus = '';
    summary = '';
  }

  const isApproved = validationStatus === 'APPROVED';
  const statusLabel = isApproved ? 'Approved' : 'Changes Requested';

  let isSuccess = false;
  let errorMessage = '';
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
      if (!isSuccess) {
        errorMessage = parsed?.error || 'Failed to complete validation';
      }
    } catch {
      isSuccess = false;
    }
  }

  const hasError = isError || (isComplete && !isSuccess);

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning
          ? 'bg-primary/10 border-b border-primary/20'
          : 'bg-muted/50 border-b border-border'
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  Saving validation results...
                </p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Data Source Validation
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    {errorMessage || 'Failed to complete validation'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Data Source Validation
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isApproved ? (
                    <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  ) : (
                    <AlertTriangleIcon className="h-4 w-4 text-warning flex-shrink-0" />
                  )}
                  <p className="text-sm m-0 text-foreground">
                    Feedback saved — data source {statusLabel.toLowerCase()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        {summary && !isRunning && !hasError && (
          <p className="text-xs text-muted-foreground mt-1 truncate">
            {summary}
          </p>
        )}
      </div>
    </Card>
  );
};

export const CompleteValidationToolCall = CompleteValidationToolCallComponent as React.ComponentType<any>;
